import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import TagsInput from "@/components/TagsInput";
import { useTRPC } from "@/trpc";
import { Check, Share2 } from "lucide-react";
import { PROFILE_FORM_STYLES } from "@/components/Forms/profile-form-styles";
import { cn } from "@/lib/utils";

const formSchema = z.object({
  emails: z
    .array(z.string().email("Please enter a valid email address"))
    .min(1, "Please enter at least one email address"),
});

type FormValues = z.infer<typeof formSchema>;

interface SharingProfileModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  appearance?: "default" | "profile";
}
export function SharingProfileModal({
  open,
  onOpenChange,
  userId,
  appearance = "default",
}: SharingProfileModalProps) {
  const isProfile = appearance === "profile";
  const [sharedEmails, setSharedEmails] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const trpc = useTRPC();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      emails: [],
    },
  });

  const mutation = useMutation(
    trpc.profile.shareProfile.mutationOptions({
      onSuccess: () => {
        setError(null);
        const emails = form.getValues("emails");
        setSharedEmails(emails);
        form.reset();
      },
      onError: () => {
        setError(
          "Failed to share profile. Please check the email and try again.",
        );
      },
    }),
  );

  function onSubmit(values: FormValues) {
    setError(null);

    mutation.mutate({
      emails: values.emails,
      includeFiles: true,
      shareWithOthers: true,
      sourceUserId: userId,
    });
  }

  function handleClose() {
    setSharedEmails(null);
    setError(null);
    form.reset();
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className={cn(
          "sm:max-w-[425px] rounded-lg border shadow-lg bg-white dark:bg-gray-900",
          isProfile && PROFILE_FORM_STYLES.dialog,
          isProfile && "sm:[&.dialog-content]:max-w-lg dark:bg-slate-50",
        )}
      >
        {sharedEmails ? (
          <div
            className={cn(
              "space-y-8 py-6",
              isProfile && PROFILE_FORM_STYLES.body,
            )}
          >
            <div className="flex flex-col items-center justify-center space-y-4 text-center">
              <div
                className={cn(
                  "flex size-16 items-center justify-center rounded-full bg-green-100 text-green-600",
                  isProfile && "rounded-2xl bg-emerald-50 text-emerald-600",
                )}
              >
                <Check aria-hidden="true" className="size-8" />
              </div>
              <div className="space-y-2">
                <DialogTitle
                  className={cn(
                    "text-2xl font-semibold tracking-tight",
                    isProfile && PROFILE_FORM_STYLES.title,
                  )}
                >
                  Profile shared
                </DialogTitle>
                <DialogDescription
                  className={cn(
                    "text-sm text-gray-500",
                    isProfile && PROFILE_FORM_STYLES.description,
                  )}
                >
                  Your profile has been shared with:
                </DialogDescription>
                <p
                  className={cn(
                    "text-base font-medium",
                    isProfile && "break-words text-sm text-slate-700",
                  )}
                >
                  {sharedEmails.join(", ")}
                </p>
              </div>
            </div>
            <Button
              onClick={handleClose}
              className={cn(
                "w-full h-12 text-base font-medium transition-colors",
                isProfile && PROFILE_FORM_STYLES.primaryButton,
              )}
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader
              className={cn(
                "space-y-3 pb-4",
                isProfile && PROFILE_FORM_STYLES.header,
              )}
            >
              <div className={cn(isProfile && "flex items-start gap-4")}>
                {isProfile && (
                  <div className={PROFILE_FORM_STYLES.icon}>
                    <Share2 aria-hidden="true" className="size-5" />
                  </div>
                )}
                <div className="space-y-1.5">
                  {isProfile && (
                    <p className={PROFILE_FORM_STYLES.eyebrow}>Your profile</p>
                  )}
                  <DialogTitle
                    className={cn(
                      "text-2xl font-semibold tracking-tight",
                      isProfile && PROFILE_FORM_STYLES.title,
                    )}
                  >
                    Share your profile
                  </DialogTitle>
                  <DialogDescription
                    className={cn(
                      "text-sm text-gray-500",
                      isProfile && PROFILE_FORM_STYLES.description,
                    )}
                  >
                    Send your profile and documents to a trusted contact.
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className={cn(
                  "space-y-6",
                  isProfile &&
                    "flex min-h-0 flex-1 flex-col space-y-0 overflow-hidden",
                )}
              >
                <div
                  className={cn(
                    "space-y-6",
                    isProfile && PROFILE_FORM_STYLES.body,
                  )}
                >
                  <div
                    className={cn(
                      "space-y-5",
                      isProfile && PROFILE_FORM_STYLES.section,
                    )}
                  >
                    <FormField
                      control={form.control}
                      name="emails"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel
                            htmlFor="profile-sharing-emails"
                            className={cn(
                              "text-sm font-medium",
                              isProfile && PROFILE_FORM_STYLES.label,
                            )}
                          >
                            Email addresses
                          </FormLabel>
                          <TagsInput
                            inputId="profile-sharing-emails"
                            appearance={appearance}
                            onTagsChange={field.onChange}
                            validateValue={(value) => {
                              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                              return emailRegex.test(value);
                            }}
                            placeholder="Type email and press Enter"
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <ul
                      className={cn(
                        "list-disc space-y-2 pl-4 text-left text-sm",
                        isProfile && "text-xs leading-relaxed text-slate-500",
                      )}
                    >
                      <li>Recipients can access your profile and documents.</li>
                      <li>
                        Use it for job applications and credential verification.
                      </li>
                      <li>Only share with people you trust.</li>
                    </ul>
                  </div>
                  {error && (
                    <div
                      role="alert"
                      className={cn(
                        "text-sm text-red-600 font-medium",
                        isProfile && PROFILE_FORM_STYLES.error,
                      )}
                    >
                      {error}
                    </div>
                  )}
                </div>
                <div className={cn(isProfile && PROFILE_FORM_STYLES.footer)}>
                  {isProfile && (
                    <Button
                      type="button"
                      variant="outline"
                      className={PROFILE_FORM_STYLES.secondaryButton}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    type="submit"
                    disabled={mutation.isPending}
                    className={cn(
                      "w-full h-12 text-base font-medium transition-colors",
                      isProfile && PROFILE_FORM_STYLES.primaryButton,
                      isProfile && "sm:w-auto",
                    )}
                  >
                    {mutation.isPending ? "Sharing…" : "Share profile"}
                  </Button>
                </div>
              </form>
            </Form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
